import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { SystemSettings } from '@/types'
import { settingsService } from '@/services/settingsService'

interface SystemContextType {
  systemSettings: SystemSettings | null
  isLoading: boolean
  refreshSystemSettings: () => Promise<void>
}

const SystemContext = createContext<SystemContextType | undefined>(undefined)

interface SystemProviderProps {
  children: ReactNode
}

export const SystemProvider: React.FC<SystemProviderProps> = ({ children }) => {
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadSystemSettings = async () => {
    try {
      setIsLoading(true)
      
      // 優先使用公開API獲取軟體名稱（不需要身份驗證）
      try {
        const publicSettings = await settingsService.getPublicSoftwareName()
        setSystemSettings({
          softwareName: publicSettings.softwareName
        })
        return
      } catch (publicError) {
        console.warn('公開API載入失敗，嘗試使用已登入用戶API:', publicError)
        
        // 如果公開API失敗，嘗試已登入用戶的API
        try {
          const settings = await settingsService.getSystemSettings()
          setSystemSettings(settings)
          return
        } catch (privateError) {
          console.warn('已登入用戶API載入失敗:', privateError)
        }
      }
      
      // 如果都失敗，使用預設值
      setSystemSettings({
        softwareName: 'AnyTime'
      })
    } catch (error) {
      // 最終錯誤處理，使用預設值
      console.warn('載入系統設定失敗，使用預設值:', error)
      setSystemSettings({
        softwareName: 'AnyTime'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const refreshSystemSettings = async () => {
    await loadSystemSettings()
  }

  useEffect(() => {
    loadSystemSettings()
  }, [])

  return (
    <SystemContext.Provider value={{
      systemSettings,
      isLoading,
      refreshSystemSettings
    }}>
      {children}
    </SystemContext.Provider>
  )
}

export const useSystem = (): SystemContextType => {
  const context = useContext(SystemContext)
  if (context === undefined) {
    throw new Error('useSystem must be used within a SystemProvider')
  }
  return context
}

export default SystemContext
